import React, { useState } from 'react';

import { AutoLink } from '@betterment/js-runtime';
import { Icon, Box as SambaBox, Spinner } from '@betterment/samba-react';
import { css } from '@betterment/samba-styled-system/css';
import { Box } from '@betterment/style-closet-react';

import {
  ClickAwayOverlay,
  CollapsedHeader,
  CtaButton,
  Hamburger,
  NavigationContainer,
  Sidebar,
} from './Navigation/styled-components';

import { useCurrentAdvisor } from '../../hooks/useCurrentAdvisor';
import urlBuilder from '../../Shared/url-builder';
import SidebarNav from '../SidebarNav';

type LogoButtonProps = {
  logoPath: string | null | undefined;
  isLogoKnockedOut: boolean;
  destinationPath: string;
  firmName: string;
};

const LogoButton: React.FC<LogoButtonProps> = ({ logoPath, isLogoKnockedOut, destinationPath, firmName }) => {
  if (!logoPath) {
    return (
      <AutoLink to={destinationPath} color="bmtWhite">
        {firmName}
      </AutoLink>
    );
  }
  const logoImg = <img className={css({ height: 'size.full' })} alt="logo" src={logoPath} />;

  return (
    <AutoLink to={destinationPath}>
      {isLogoKnockedOut ? (
        logoImg
      ) : (
        <Box bg="bmtWhite" boxShadow="default">
          {logoImg}
        </Box>
      )}
    </AutoLink>
  );
};

export const Navigation: React.FC<{ active: string }> = ({ active }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { currentAdvisor } = useCurrentAdvisor();

  if (!currentAdvisor) {
    return (
      <SambaBox
        css={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          backgroundColor: 'background.abyss',
        }}
      >
        <Spinner />
      </SambaBox>
    );
  }

  const hasKnockedOutLogo = Boolean(currentAdvisor.logo?.mediumKnockout);
  const mediumLogoPath = hasKnockedOutLogo ? currentAdvisor.logo?.mediumKnockout : currentAdvisor.logo?.medium;
  const largeLogoPath = hasKnockedOutLogo ? currentAdvisor.logo?.largeKnockout : currentAdvisor.logo?.large;

  const logoButton = {
    mediumFirmLogoPath: mediumLogoPath,
    largeFirmLogoPath: largeLogoPath,
    logoIsKnockedOut: hasKnockedOutLogo,
    destinationPath: urlBuilder.getSummaryPage.absolute(),
    firmName: currentAdvisor.firm.name,
  };

  return (
    <NavigationContainer>
      <CollapsedHeader>
        <Hamburger onClick={() => setIsOpen(true)} role="button" aria-label="Open menu">
          <Icon name="Hamburger" size="xxsmall" color="icon.onPrimary" />
        </Hamburger>

        <LogoButton
          logoPath={logoButton.mediumFirmLogoPath}
          destinationPath={logoButton.destinationPath}
          isLogoKnockedOut={logoButton.logoIsKnockedOut}
          firmName={currentAdvisor.firm.name}
        />

        <AutoLink to={currentAdvisor.firm.startClientInvitePath}>
          <CtaButton>
            <Icon name="Mail" size="xxsmall" color="icon.onPrimary" />
          </CtaButton>
        </AutoLink>
      </CollapsedHeader>
      {isOpen && <ClickAwayOverlay onClick={() => setIsOpen(false)} />}
      <Sidebar open={isOpen}>
        <SidebarNav
          active={active}
          isAdmin={currentAdvisor.isAdmin}
          isCompliance={currentAdvisor.isCompliance}
          isFourOhOneKEnabled={currentAdvisor.firm.fourOhOneKEnabled}
          isFourOhOneKOnly={currentAdvisor.firm.fourOhOneKOnly}
          logoButton={logoButton}
        />
      </Sidebar>
    </NavigationContainer>
  );
};
